import React, { useState, useEffect } from 'react';
import { Calculator, TrendingUp, DollarSign, FileText, Zap, Building, Hammer, Target } from 'lucide-react';

const FinalEstimateLoader = ({ projectData }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [currentTip, setCurrentTip] = useState(0);
  const [animatingNumbers, setAnimatingNumbers] = useState(false);

  const steps = [
    { icon: <Calculator size={24} />, text: "Analyzing Your Selections", color: "from-blue-500 to-blue-600" },
    { icon: <TrendingUp size={24} />, text: "Calculating Market Rates", color: "from-green-500 to-green-600" },
    { icon: <Building size={24} />, text: "Processing Construction Details", color: "from-purple-500 to-purple-600" },
    { icon: <Hammer size={24} />, text: "Estimating Labor Costs", color: "from-orange-500 to-orange-600" },
    { icon: <DollarSign size={24} />, text: "Finalizing Cost Breakdown", color: "from-emerald-500 to-emerald-600" },
    { icon: <FileText size={24} />, text: "Generating Your Report", color: "from-indigo-500 to-indigo-600" },
  ];

  const tips = [
    "Your estimate includes all major construction divisions",
    "Costs are based on current market rates in your area",
    "Material prices can fluctuate - consider a 10% buffer",
    "Labor costs vary by season and contractor availability",
    "Permits and inspections are included in general requirements",
    "Energy-efficient upgrades often pay for themselves over time",
  ];

  const constructionFacts = [
    "$1.36 trillion was spent on construction in the US in 2023",
    "The average home takes 6-8 months to build",
    "Construction employs over 7.6 million people in the US",
    "Green building practices can reduce costs by 20% long-term",
  ];

  useEffect(() => {
    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev < 95) {
          const newProgress = prev + Math.random() * 8 + 2;
          return Math.min(newProgress, 100); // Cap at 100%
        }
        return Math.min(prev, 100); // Ensure it never exceeds 100%
      });
    }, 300);

    // Step progression
    const stepInterval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev < steps.length - 1) return prev + 1;
        return prev;
      });
    }, 1800);

    // Tips rotation
    const tipInterval = setInterval(() => {
      setCurrentTip(prev => (prev + 1) % tips.length);
    }, 3000);

    // Number animation effect
    const numberInterval = setInterval(() => {
      setAnimatingNumbers(prev => !prev);
    }, 1500);

    return () => {
      clearInterval(progressInterval);
      clearInterval(stepInterval);
      clearInterval(tipInterval);
      clearInterval(numberInterval);
    };
  }, [steps.length, tips.length]);

  const locationText = projectData ? 
    `${projectData.city}, ${projectData.state}` : 'Your Area';
  const projectTypeText = projectData ? 
    projectData.constructionType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Construction Project';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 sm:p-12 max-w-3xl w-full">
        
        {/* Header with animated icon */}
        <div className="text-center mb-8">
          <div className="relative inline-flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full animate-pulse opacity-20 scale-110"></div>
            <div className="relative bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full p-4 text-white shadow-lg">
              <Target size={32} className="animate-spin" style={{ animationDuration: '3s' }} />
            </div>
          </div>
          <h1 className="mt-6 text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Finalizing Your Estimate
          </h1>
          <p className="text-lg text-gray-600">
            For your <span className="font-semibold text-blue-700">{projectTypeText}</span> in{' '}
            <span className="font-semibold text-blue-700">{locationText}</span>
          </p>
        </div>

        {/* Animated Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm font-medium text-gray-700">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full transition-all duration-500 ease-out relative"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Current Step Display */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4 p-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl border border-blue-100">
            <div className={`p-3 rounded-full bg-gradient-to-r ${steps[currentStep]?.color || 'from-blue-500 to-blue-600'} text-white shadow-lg transform transition-all duration-500 hover:scale-110`}>
              {steps[currentStep]?.icon}
            </div>
            <div className="flex-1">
              <p className="text-xl font-semibold text-gray-900 transition-all duration-500">
                {steps[currentStep]?.text}
              </p>
              <div className="flex space-x-1 mt-2">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 w-8 rounded-full transition-all duration-300 ${
                      index <= currentStep ? 'bg-blue-500' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Animated Cost Preview */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
            <div className={`text-2xl font-bold text-green-600 transition-all duration-300 ${animatingNumbers ? 'scale-105' : ''}`}>
              ${(Math.random() * 500000 + 200000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </div>
            <div className="text-xs text-green-700 font-medium">Estimated Range</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
            <div className={`text-2xl font-bold text-blue-600 transition-all duration-300 ${animatingNumbers ? 'scale-105' : ''}`}>
              {Math.floor(Math.random() * 3000 + 1500).toLocaleString()}
            </div>
            <div className="text-xs text-blue-700 font-medium">Sq Ft</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-100">
            <div className={`text-2xl font-bold text-purple-600 transition-all duration-300 ${animatingNumbers ? 'scale-105' : ''}`}>
              ${Math.floor(Math.random() * 100 + 150)}
            </div>
            <div className="text-xs text-purple-700 font-medium">Per Sq Ft</div>
          </div>
        </div>

        {/* Rotating Tips */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start space-x-3">
            <Zap size={20} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800 text-sm mb-1">Pro Tip:</p>
              <p className="text-sm text-amber-700 transition-all duration-500">
                {tips[currentTip]}
              </p>
            </div>
          </div>
        </div>

        {/* Construction Fact */}
        <div className="text-center">
          <p className="text-xs text-gray-500 italic">
            💡 Did you know? {constructionFacts[currentStep % constructionFacts.length]}
          </p>
        </div>

        {/* Subtle completion indicator */}
        <div className="flex justify-center mt-6">
          <div className="flex space-x-2">
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i <= currentStep ? 'bg-blue-500 animate-pulse' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalEstimateLoader;
